import React, { useState } from "react";
import styled from "styled-components";
import { device } from "../../utils/device";
import {
  Box,
  Modal,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import TextView from "../TextView";
import Button from "../Button";
import useScreenSize from "../../hooks/useScreenSize";
import { ASSETS } from "../../utils/config";
import { colors } from "../../utils";
import { isInAppBrowser } from "../../utils/browserDetection";
import { Cancel } from "../../assets/icons";
import { ReactComponent as AppleIcon } from "@assets/icons/mobilePopupIcons/ic_round-apple.svg";
import { ReactComponent as PlayStoreIcon } from "@assets/icons/mobilePopupIcons/playstore.svg";
import { useStoreEmailReferralCode } from "../../services/mutations";
import WheelComponent from "../WheelComponent";

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContainer = styled.div`
  position: relative;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  padding: 16px;
  width: 90vw;
  max-width: 500px;
  text-align: center;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-direction: column;
  max-height: 90vh;
  overflow-y: auto;
  margin: 16px;
`;

const StyledImage = styled.img`
  width: 100px;
  height: 100px;
  margin-bottom: 0.5rem;
`;

const ContentContainer = styled.div`
  width: 100%;
  margin: 8px 0 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BenefitsSection = styled.div`
  width: 100%;
  border-radius: 8px;
  margin-top: 16px;
  text-align: left;
`;

const BenefitsList = styled.ul`
  padding-left: 0px;
  list-style: none;

  li {
    position: relative;
    padding-left: 20px;
    margin-bottom: 4px;
    color: #333333;

    &:before {
      content: "•";
      position: absolute;
      left: 0;
      top:-3px;
      font-weight: bold;
      font-size: 16px;
    }
  }
`;

const EmailInput = styled(TextField)`
  width: 100%;

  & .MuiOutlinedInput-root {
    border-radius: 8px;
    height: 45px;

    & fieldset {
      border-color: ${(props) => (props.error ? "#FF3933" : "#dfe2e6")};
      border-width: 1px;
    }

    &:hover fieldset {
      border-color: ${(props) => (props.error ? "#FF3933" : "#dfe2e6")};
    }

    &.Mui-focused fieldset {
      border-color: ${(props) => (props.error ? "#FF3933" : "#9b5af8")};
    }
  }

  & .MuiOutlinedInput-input {
    padding: 12px;
    font-family: "DM Sans", sans-serif;
    font-size: 14px;

    &::placeholder {
      color: #999999;
      opacity: 1;
    }
  }
`;

const ErrorText = styled(Typography)`
  color: #ff3933;
  font-size: 15px;
  text-align: left;
  margin-top: 4px;
  font-family: "DM Sans", sans-serif;
`;

const GetAppButton = styled.button`
  background-color: #181819;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 12px 24px;
  font-family: "DM Sans", sans-serif;
  font-weight: 500;
  font-size: 14px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #1f1f1f;
  }

  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
`;

const LoginButton = styled.button`
  background-color: #9b5af8;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 12px 24px;
  font-family: "DM Sans", sans-serif;
  font-weight: 500;
  font-size: 14px;
  flex: 1;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #7f47d8;
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;
  margin-top: 24px;
  flex-direction: row;
  justify-content: space-evenly;

  /* Reverse button order on mobile for better UX */
  @media ${device.mobilePortrait} {
    flex-direction: column-reverse;
    gap: 12px;
  }
`;

const CloseIcon = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  @media ${device.mobilePortrait} {
    top: 12px;
    right: 12px;
  }
`;

const MobileModal = ({
  open,
  handleClose,
  disableClose,
  title1,
  title2,
  imageSrc,
  primaryButtonTitle,
  primaryButtonOnClick,
  secondaryButtonTitle,
  secondaryButtonOnClick,
  wheelData,
  profileData,
}) => {
  const [email, setEmail] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [showInAppWarning, setShowInAppWarning] = useState(false);
  const storeEmailReferralCode = useStoreEmailReferralCode();
  const theme = useTheme();
  // Utility function to detect iOS
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  };

  const getReferralCode = () => {
    // Check sessionStorage first, then localStorage
    let referralData = sessionStorage.getItem("referralData");
    if (!referralData) {
      referralData = localStorage.getItem("referralData");
    }

    if (referralData) {
      try {
        const parsedData = JSON.parse(referralData);
        return parsedData.referral_code || null;
      } catch (error) {
        console.error("Error parsing referralData:", error);
        return null;
      }
    }
    return null;
  };

  const handleGetAppClick = () => {
    setShowEmailForm(true);
  };

  const handleEmailSubmit = async () => {
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      setEmailError("Email is required");
      return;
    }
    if (!emailRegex.test(email)) {
      setEmailError("This email is invalid");
      return;
    }

    setEmailError("");
    setIsSubmitting(true);

    try {
      const referralCode = getReferralCode();

      if (referralCode) {
        // Call the API to store email with referral code
        await storeEmailReferralCode.mutateAsync({
          email: email.trim(),
          referral_code: referralCode,
        });
      }
      // On success, show download button
      setEmailSubmitted(true);
    } catch (error) {
      console.error("Error submitting email:", error);
      const resp = error?.response;

      // If API returns 400 with the specific email message, treat as success
      if (resp && (resp.status === 400 || resp.status_code === 400)) {
        const respStr = JSON.stringify(resp.data || {});
        if (
          respStr.includes(
            "Email Referral Code with this Email Address already exists"
          )
        ) {
          // The email was already stored previously — show download button
          setEmailSubmitted(true);
        } else if (resp?.data?.message) {
          setEmailError(resp.data.message);
        } else {
          setEmailError("Something went wrong. Please try again.");
        }
      } else if (resp?.status === 409) {
        // Duplicate email error mapped to 409 in other flows
        setEmailError("This email already has a referral code stored.");
      } else if (resp?.data?.message) {
        setEmailError(resp.data.message);
      } else {
        setEmailError("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadClick = () => {
    const appStoreUrl =
      "https://apps.apple.com/pk/app/iyuga-wheel/id6746421146";
    const playStoreUrl =
      "https://play.google.com/store/apps/details?id=com.iyuga";

    // Directly redirect to stores
    if (isIOS()) {
      window.location.href = appStoreUrl;
    } else {
      window.location.href = playStoreUrl;
    }
  };

  // old implementation of opening the app
  // const handleOpenAppClick = () => {
  //   const iosDeepLink = "iyuga://"; // Custom URL scheme for iOS fallback
  //   const androidDeepLink =
  //     "intent://#Intent;scheme=iyuga;package=com.iyuga;end";

  //   // Track if app was opened
  //   let appOpened = false;
  //   let timeoutId;

  //   // Handler for when app opens (page loses focus)
  //   const handleBlur = () => {
  //     appOpened = true;
  //     clearTimeout(timeoutId);
  //     window.removeEventListener("blur", handleBlur);
  //     document.removeEventListener("visibilitychange", handleVisibilityChange);
  //     handleClose();
  //   };

  //   // Handler for visibility change (more reliable on mobile)
  //   const handleVisibilityChange = () => {
  //     if (document.hidden) {
  //       appOpened = true;
  //       clearTimeout(timeoutId);
  //       window.removeEventListener("blur", handleBlur);
  //       document.removeEventListener(
  //         "visibilitychange",
  //         handleVisibilityChange
  //       );
  //       handleClose();
  //     }
  //   };

  //   // Set up listeners
  //   window.addEventListener("blur", handleBlur);
  //   document.addEventListener("visibilitychange", handleVisibilityChange);

  //   // Try to open the app via universal link
  //   if (isIOS()) {
  //     window.location.href = iosDeepLink;
  //   } else {
  //     window.location.href = androidDeepLink;
  //   }

  //   // Clean up listeners after timeout
  //   timeoutId = setTimeout(() => {
  //     window.removeEventListener("blur", handleBlur);
  //     document.removeEventListener("visibilitychange", handleVisibilityChange);
  //   }, 2500); // 2.5 second timeout
  // };

  const getOpenAppUrl = () => window.location.href;

  const handleOpenAppClick = () => {
    if (isInAppBrowser()) {
      setShowInAppWarning(true);
      return;
    }

    const appStoreUrl =
      "https://apps.apple.com/pk/app/iyuga-wheel/id6746421146";
    const iosDeepLink = "iyuga://";
    const androidDeepLink =
      "intent://#Intent;scheme=iyuga;package=com.iyuga;end";

    if (isIOS()) {
      let timeoutId;
      const handleVisibilityChange = () => {
        if (document.hidden) {
          clearTimeout(timeoutId);
          document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange
          );
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.location.href = iosDeepLink;
      timeoutId = setTimeout(() => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        window.location.href = appStoreUrl;
      }, 2000);
    } else {
      window.location.href = androidDeepLink;
    }
  };

  const handleCancel = () => {
    if (showEmailForm) {
      setShowEmailForm(false);
      setEmail("");
      setEmailError("");
      setEmailSubmitted(false);
    } else {
      if (secondaryButtonOnClick) secondaryButtonOnClick();
      !disableClose && handleClose();
    }
  };

  return (
    <StyledModal
      open={open}
      onClose={disableClose ? undefined : handleClose}
      aria-labelledby="general-dialog-title"
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <ModalContainer
        style={{
          position: "relative",
        }}
      >
        {!disableClose && (
          <CloseIcon>
            <Cancel onClick={handleClose} />
          </CloseIcon>
        )}
        <>
          <WheelComponent
            userData={{
              id: Math.random(),
              url: profileData?.data?.profile_image || ASSETS.iyugaLogo,
            }}
            maxSelectedArcs={0}
            arcThickness={-10}
            chartData={wheelData || []}
            margin={25}
            setChartData={() => { }}
            containerDivStyle={{ width: "140px", height: "140px" }}
          />


          <TextView
            variant="title"
            style={{ fontSize: "20px", color: "#123051", fontWeight: 700 }}
          >
            {showEmailForm
              ? emailSubmitted
                ? "Ready to download!"
                : "Providing your email lets us give credit to the owner of this Gallery"
              : "Want to see this Wheel Gallery?"}
          </TextView>

          {!showEmailForm && (
            <>
              <TextView
                variant="largeText"
                style={{
                  textAlign: "center",
                  fontSize: "15px",
                  color: "#123051",
                }}
              >
                Download the Free iYuga mobile App!
              </TextView>
              <TextView
                style={{ fontSize: 12, fontStyle: "italic", fontWeight: 400 }}
              >
                Or view it on your desktop's web browser.
              </TextView>

              <BenefitsSection>
                <BenefitsList>
                  <li>
                    <TextView variant="largeText" style={{ fontSize: "12px" }}>
                      No bait & switch! Our <b>Free</b> version is great!
                    </TextView>
                  </li>
                  <li>
                    <TextView variant="largeText" style={{ fontSize: "12px" }}>
                      Wheel Building Wizards can enjoy 50% off!
                    </TextView>
                  </li>
                </BenefitsList>
              </BenefitsSection>
            </>
          )}

          {showEmailForm && (
            <>
              {!emailSubmitted ? (
                <>
                  <TextView
                    variant="largeText"
                    style={{
                      textAlign: "center",
                      fontSize: "15px",
                      color: "#123051",
                      marginBottom: "16px",
                    }}
                  >
                    <b>Don't worry!</b> We won't save your info if you don't
                    create an account within 24 hours.
                  </TextView>

                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                    }}
                  >
                    <Box>
                      <EmailInput
                        placeholder="Enter email you will use for the app"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (emailError) setEmailError(""); // Clear error on typing
                        }}
                        type="email"
                        error={!!emailError}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !isSubmitting) {
                            handleEmailSubmit();
                          }
                        }}
                      />
                      {emailError && <ErrorText>{emailError}</ErrorText>}
                    </Box>

                    <Button
                      style={{ backgroundColor: "black", color: "white" }}
                      onClick={handleEmailSubmit}
                      isLoading={isSubmitting}
                    >
                      {!isSubmitting ? "Submit" : ""}
                    </Button>

                    <TextView
                      variant="largeText"
                      style={{
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#123051",
                        marginTop: "8px",
                      }}
                    >
                      To continue on this browser:
                    </TextView>

                    <Box sx={{ display: "flex", gap: "12px", width: "100%" }}>
                      <Button
                        variant="outlined"
                        onClick={handleCancel}
                        style={{ flex: 1 }}
                      >
                        Cancel
                      </Button>
                      <LoginButton
                        onClick={() => {
                          // Navigate to login
                          window.location.href = "/login";
                        }}
                      >
                        Log in
                      </LoginButton>
                    </Box>
                  </Box>
                </>
              ) : (
                <>
                  <TextView
                    variant="largeText"
                    style={{
                      textAlign: "center",
                      fontSize: "15px",
                      color: "#123051",
                      marginBottom: "24px",
                    }}
                  >
                    Thank you! Click below to download the app.
                  </TextView>

                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                    }}
                  >
                    <GetAppButton onClick={handleDownloadClick}>
                      {isIOS() ? (
                        <>
                          <AppleIcon style={{ width: 20, height: 20 }} />
                          Download now
                        </>
                      ) : (
                        <>
                          <PlayStoreIcon style={{ width: 20, height: 20 }} />
                          Download now
                        </>
                      )}
                    </GetAppButton>

                    <TextView
                      variant="largeText"
                      style={{
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#123051",
                        marginTop: "8px",
                      }}
                    >
                      To continue on this browser:
                    </TextView>

                    <Box sx={{ display: "flex", gap: "12px", width: "100%" }}>
                      <Button
                        variant="outlined"
                        onClick={handleCancel}
                        style={{ flex: 1 }}
                      >
                        Cancel
                      </Button>
                      <LoginButton
                        onClick={() => {
                          // Navigate to login
                          window.location.href = "/login";
                        }}
                      >
                        Log in
                      </LoginButton>
                    </Box>
                  </Box>
                </>
              )}
            </>
          )}

          {!showEmailForm && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                width: "100%",
              }}
            >
              <GetAppButton onClick={handleGetAppClick}>
                {isIOS() ? (
                  <>
                    <AppleIcon style={{ width: 20, height: 20 }} />
                    Get the App
                  </>
                ) : (
                  <>
                    <PlayStoreIcon style={{ width: 20, height: 20 }} />
                    Get the App
                  </>
                )}
              </GetAppButton>

              {!showInAppWarning && (
                <Button onClick={handleOpenAppClick}>{isInAppBrowser() ? "Open iYuga App" : "Already have the mobile app? Open here."}</Button>
              )}

              {showInAppWarning && (
                <div style={{borderRadius: "8px", padding: "8px", backgroundColor: colors.PrimaryPurple100, border: `2px solid ${colors.PrimaryPurple500}`, paddingTop: "16px"}}>
                  <TextView
                    variant="largeText"
                    style={{
                      textAlign: "center",
                      fontSize: "13px",
                      marginTop: "-8px",
                    }}
                  >
                    Tap the three dots in the corner of your screen and click <b>"Open External Browser"</b>.
                  </TextView>
                </div>
              )}

              <Button variant="outlined" onClick={handleClose}>
                View on Web
              </Button>
            </Box>
          )}
        </>
      </ModalContainer>
    </StyledModal>
  );
};

export default MobileModal;
